import React, { useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight,
  Brain,
  Heart,
  Users,
  Shield,
  Compass,
  Lightbulb,
  ChevronDown,
  Send,
  Phone,
  Mail,
  MapPin,
  Check,
  Loader2,
  Handshake,
  Leaf,
  CircleDot,
  FileText,
  Download,
  Instagram,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ─── Animation Variants ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

/* ─── Animated Section Wrapper ─── */
function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
        },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Navigation ─── */
function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setDropdownOpen(false);
    setMobileOpen(false);
  };

  const dropdownItems = [
    { label: "Philosophy", id: "philosophy" },
    { label: "Who We're For", id: "whowerefor" },
    { label: "Approach & Commitment", id: "approach" },
    { label: "What to Expect", id: "whattoexpect" },
  ];

  return (
    <motion.nav
      data-testid="navigation"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F7F5F0]/80 backdrop-blur-xl border-b border-white/20 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-20">
        <button
          onClick={() => scrollToSection("hero")}
          className="font-['Cormorant_Garamond'] text-2xl font-light tracking-tight text-[#1A1C19] hover:text-[#4A6741] transition-colors"
          data-testid="brand-logo"
        >
          ALIGN<span className="text-[#4A6741]">&bull;</span>
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-10">
          {/* About Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setDropdownOpen(true)}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            <button
              className="animated-underline text-xs uppercase tracking-[0.2em] font-medium text-[#5C635A] hover:text-[#1A1C19] transition-colors inline-flex items-center gap-1.5"
              data-testid="nav-about-dropdown"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              About
              <ChevronDown
                size={12}
                className={`transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""}`}
              />
            </button>

            {/* Dropdown Panel */}
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 transition-all duration-300 ${
                dropdownOpen
                  ? "opacity-100 translate-y-0 pointer-events-auto"
                  : "opacity-0 -translate-y-2 pointer-events-none"
              }`}
            >
              <div className="bg-white/90 backdrop-blur-xl border border-white/40 rounded-2xl shadow-lg overflow-hidden min-w-[220px]">
                {dropdownItems.map((item, i) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`block w-full text-left px-5 py-3 text-xs uppercase tracking-[0.15em] font-medium text-[#5C635A] hover:text-[#1A1C19] hover:bg-[#4A6741]/5 transition-colors ${
                      i < dropdownItems.length - 1 ? "border-b border-[#EAE6DF]/50" : ""
                    }`}
                    data-testid={`nav-dropdown-${item.id}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={() => scrollToSection("services")}
            className="animated-underline text-xs uppercase tracking-[0.2em] font-medium text-[#5C635A] hover:text-[#1A1C19] transition-colors"
            data-testid="nav-services"
          >
            Services
          </button>
          <a
            href="/resources"
            className="animated-underline text-xs uppercase tracking-[0.2em] font-medium text-[#5C635A] hover:text-[#1A1C19] transition-colors"
            data-testid="nav-resources"
          >
            Resources
          </a>
          <button
            onClick={() => scrollToSection("contact")}
            className="animated-underline text-xs uppercase tracking-[0.2em] font-medium text-[#5C635A] hover:text-[#1A1C19] transition-colors"
            data-testid="nav-contact"
          >
            Contact
          </button>
          <button
            onClick={() => scrollToSection("contact")}
            className="btn-pill text-[11px]"
            data-testid="nav-cta-button"
          >
            Join Waiting List
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden flex flex-col gap-1.5 p-2"
          data-testid="mobile-menu-button"
        >
          <span className={`block w-5 h-[1.5px] bg-[#1A1C19] transition-all duration-300 ${mobileOpen ? "rotate-45 translate-y-[4.5px]" : ""}`} />
          <span className={`block w-5 h-[1.5px] bg-[#1A1C19] transition-all duration-300 ${mobileOpen ? "opacity-0" : ""}`} />
          <span className={`block w-5 h-[1.5px] bg-[#1A1C19] transition-all duration-300 ${mobileOpen ? "-rotate-45 -translate-y-[4.5px]" : ""}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-400 overflow-hidden ${
          mobileOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="bg-[#F7F5F0]/95 backdrop-blur-xl border-t border-[#EAE6DF]/50 px-6 py-6 space-y-1">
          <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#4A6741] mb-2 px-3">
            About
          </p>
          {dropdownItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className="block w-full text-left px-3 py-2.5 text-sm font-light text-[#5C635A] hover:text-[#1A1C19] hover:bg-[#4A6741]/5 rounded-xl transition-colors"
              data-testid={`mobile-nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
          <div className="section-divider my-3 mx-3" />
          <button
            onClick={() => scrollToSection("services")}
            className="block w-full text-left px-3 py-2.5 text-sm font-light text-[#5C635A] hover:text-[#1A1C19] hover:bg-[#4A6741]/5 rounded-xl transition-colors"
          >
            Services
          </button>
          <a
            href="/resources"
            className="block w-full text-left px-3 py-2.5 text-sm font-light text-[#5C635A] hover:text-[#1A1C19] hover:bg-[#4A6741]/5 rounded-xl transition-colors"
          >
            Resources
          </a>
          <button
            onClick={() => scrollToSection("contact")}
            className="block w-full text-left px-3 py-2.5 text-sm font-light text-[#5C635A] hover:text-[#1A1C19] hover:bg-[#4A6741]/5 rounded-xl transition-colors"
          >
            Contact
          </button>
          <div className="pt-3 px-3">
            <button
              onClick={() => scrollToSection("contact")}
              className="btn-pill w-full justify-center text-[11px]"
            >
              Join Waiting List
            </button>
          </div>
        </div>
      </div>
    </motion.nav>
  );
}

/* ─── Hero Section ─── */
function HeroSection() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-end pb-24 md:pb-32 overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1759080135394-aa8333a9ff5e?w=1920&q=80"
          alt="Calming water ripples"
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.p
            variants={fadeUp}
            className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-6"
          >
            Integration Psychology and Support Practice
          </motion.p>
          <motion.h1
            variants={fadeUp}
            className="font-['Cormorant_Garamond'] text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-[#1A1C19] mb-8 leading-[1.05]"
            data-testid="hero-tagline"
          >
            Created for people{" "}
            <br />
            bridging insight and everyday life
          </motion.h1>
          <motion.p
            variants={fadeUp}
            className="text-lg md:text-xl font-light text-[#5C635A] max-w-xl mb-10 leading-relaxed"
          >
            Psychology, in context. Bridging the gap between knowing
            and living. Support that creates sustainable change beyond the session.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
            <button
              onClick={() =>
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="btn-pill"
              data-testid="hero-cta-button"
            >
              Join The Waiting List
              <ArrowRight size={14} />
            </button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown size={20} className="text-[#4A6741] opacity-50" />
        </motion.div>
      </motion.div>
    </section>
  );
}

/* ─── Philosophy Section ─── */
function PhilosophySection() {
  return (
    <section
      id="philosophy"
      data-testid="philosophy-section"
      className="py-24 md:py-36 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <AnimatedSection>
            <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-6">
              Our Philosophy
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light tracking-tight text-[#1A1C19] mb-8 leading-[1.1]">
              Psychology,
              <br />
              <span className="italic text-[#4A6741]">in context</span>
            </h2>
            <div className="section-divider mb-8" />
            <p className="text-lg font-light text-[#5C635A] leading-relaxed mb-6">
              Most people don't need more insight.
              They need support that holds up in their actual lives.
            </p>
            <p className="text-lg font-light text-[#5C635A] leading-relaxed mb-6">
              At ALIGN&bull;, we focus on what happens beyond the session —
              in your routines, your relationships, and the environments you
              move through every day.
            </p>
            <p className="text-lg font-light text-[#5C635A] leading-relaxed mb-6">
              We bring together formulation-led psychology, lived experience,
              and real-life support so that understanding becomes something
              you can actually use.
            </p>
            <p className="text-lg font-light text-[#5C635A] leading-relaxed">
              Because change doesn't happen in isolation.
              It happens in context.
            </p>
            <div className="mt-8 bg-[#4A6741]/5 rounded-2xl px-6 py-5">
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#4A6741] mb-2">
                Our Mission
              </p>
              <p className="font-['Cormorant_Garamond'] text-xl italic text-[#1A1C19] leading-relaxed">
                To help mental health support become more connected,
                more human, and more usable within everyday life.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2} className="relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5]">
              <img
                src="https://customer-assets.emergentagent.com/job_wellness-journey-231/artifacts/9vtdsveh_ChatGPT%20Image%20May%2013%2C%202026%2C%2001_15_23%20PM.png"
                alt="ALIGN practice interior — calming, sensory-aware space"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#4A6741]/20 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 glass-card p-6 max-w-[240px]">
              <p className="font-['Cormorant_Garamond'] text-xl italic text-[#4A6741]">
                "Where understanding becomes usable."
              </p>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

/* ─── Services Section ─── */
const services = [
  {
    icon: Brain,
    title: "Formulation-Led Psychology",
    description:
      "Collaborative, contextual understanding of you — not just your symptoms or a diagnostic label. We build a shared picture of what's happening and why, within the full context of your life.",
  },
  {
    icon: Handshake,
    title: "Integration Practitioners",
    description:
      "A role designed to bridge clinical insight and daily life. Integration practitioners work alongside you — often in your own environment — to translate understanding into practical, sustainable routines.",
  },
  {
    icon: Heart,
    title: "Lived Experience",
    description:
      "Lived experience is clinical knowledge. It keeps the work human, reduces the friction of being understood from outside your reality, and is embedded throughout the practice — not treated as an add-on.",
  },
  {
    icon: Users,
    title: "Family & Carer Collaboration",
    description:
      "Working alongside families, carers, and other providers where it helps — building a connected support system and shared understanding across the people in your life.",
  },
  {
    icon: Leaf,
    title: "Nervous System-Aware Environment",
    description:
      "Our space is a therapeutic tool: warm lighting, acoustic calm, natural materials, sensory-considered zones, regulation pockets, and transition spaces. No fluorescents. No fragrance. The environment regulates before the session begins.",
  },
  {
    icon: CircleDot,
    title: "Group Programs & NDIS Support",
    description:
      "Targeted group programs for late-diagnosed AuDHD adults, parents, young adults, and burnout recovery. NDIS-registered provider offering Improved Daily Living, Capacity Building, and Psychosocial Recovery Coaching.",
  },
];

function ServicesSection() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-36 px-6 md:px-12 lg:px-24 bg-[#EAE6DF]/40"
    >
      <div className="max-w-7xl mx-auto">
        <AnimatedSection className="text-center mb-16 md:mb-20">
          <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-6">
            What We Offer
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light tracking-tight text-[#1A1C19] mb-6">
            Our Services
          </h2>
          <p className="text-lg font-light text-[#5C635A] max-w-2xl mx-auto">
            Formulation, not diagnosis. Trauma-informed and neurodiversity-affirming.
            Support designed around the whole person and their context —
            combining clinical psychology, practical integration, and community,
            under one roof and one philosophy.
          </p>
        </AnimatedSection>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={fadeUp}
              className="glass-card p-8 group"
              data-testid={`service-card-${index}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center mb-6 group-hover:bg-[#4A6741]/15 transition-colors">
                <service.icon
                  size={22}
                  className="text-[#4A6741]"
                  strokeWidth={1.5}
                />
              </div>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-normal text-[#1A1C19] mb-3">
                {service.title}
              </h3>
              <p className="text-sm font-light text-[#5C635A] leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Approach & Commitment Section ─── */
const pillars = [
  {
    number: "01",
    title: "Evidence-Based Psychology",
    description:
      "Grounded in formulation-led, context-aware psychology. We draw on established frameworks to understand not just what is happening, but why, within the full context of your life.",
  },
  {
    number: "02",
    title: "Lived Experience",
    description:
      "Your story matters. Lived experience keeps the work human, relatable, and connected to the realities you're actually living, not just what exists in theory.",
  },
  {
    number: "03",
    title: "Real-Life Support",
    description:
      "What happens after the session is what matters. We focus on what holds up in real moments, helping you build routines, navigate systems, and create change that is sustainable.",
  },
];

function ApproachSection() {
  return (
    <section
      id="approach"
      data-testid="approach-section"
      className="py-24 md:py-36 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <AnimatedSection>
              <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-6">
                Our Approach
              </p>
              <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light tracking-tight text-[#1A1C19] mb-8 leading-[1.1]">
                Three pillars.
                <br />
                <span className="italic text-[#4A6741]">Designed for real life.</span>
              </h2>
              <div className="section-divider mb-8" />
              <p className="text-lg font-light text-[#5C635A] leading-relaxed">
                ALIGN&bull; connects what is usually kept separate.
                We bridge the gap between insight and everyday life,
                bringing together formulation-led psychology, lived experience,
                and support that holds up beyond the session.
              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.3} className="mt-12">
              <div className="relative rounded-3xl overflow-hidden aspect-[16/10]">
                <img
                  src="https://images.unsplash.com/photo-1758499947613-545e373b2ee5?w=800&q=80"
                  alt="Balanced stones representing grounding"
                  className="w-full h-full object-cover"
                />
              </div>
            </AnimatedSection>
          </div>

          <div className="flex flex-col gap-8">
            {pillars.map((pillar, index) => (
              <AnimatedSection key={index} delay={index * 0.15}>
                <div className="glass-card p-8" data-testid={`pillar-${index}`}>
                  <span className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-4 block">
                    {pillar.number}
                  </span>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-normal text-[#1A1C19] mb-4">
                    {pillar.title}
                  </h3>
                  <p className="text-sm font-light text-[#5C635A] leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>

        {/* Commitment */}
        <AnimatedSection className="mt-24 md:mt-32">
          <div
            data-testid="commitment-section"
            className="glass-card p-10 md:p-16 text-center max-w-4xl mx-auto"
          >
            <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-6">
              Our Commitment
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light tracking-tight text-[#1A1C19] mb-8 leading-[1.15]">
              Sustainable change,
              <br />
              <span className="italic text-[#4A6741]">
                not just sustainable coping
              </span>
            </h2>
            <p className="text-lg font-light text-[#5C635A] leading-relaxed max-w-2xl mx-auto">
              We are committed to people-centred, trauma-informed, integrated
              care — support designed around the whole person and their context.
              Your environment, relationships, routines, and nervous system
              matter as much as what happens in the room. We don't just help you
              understand — we help you build a life where that understanding
              actually holds up.
            </p>
          </div>
        </AnimatedSection>

        {/* Download Our Approach */}
        <AnimatedSection className="mt-12 text-center">
          <a
            href="https://customer-assets.emergentagent.com/job_wellness-journey-231/artifacts/x8snyzms_ALIGN_Our_Approach%20.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-pill-outline inline-flex items-center gap-2 text-[11px]"
            data-testid="download-approach-pdf"
          >
            <FileText size={14} />
            Download Our Full Approach
          </a>
          <p className="text-xs font-light text-[#5C635A] mt-3">
            A detailed guide to how we work — our philosophy, pillars, and what to expect.
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ─── What to Expect Section ─── */
const journeyStages = [
  {
    number: "01",
    title: "Arrival & Formulation",
    timing: "Weeks 1–4",
    description:
      "Your first contact is designed to be low-friction and sensory-considered. You'll meet your psychologist for an extended intake, and together you'll co-develop a written formulation — a shared, contextual understanding of what's happening and why. If assessment is indicated, that happens here too.",
    detail: "We ask about your sensory needs at intake. First contact is with a human, not an automated system.",
  },
  {
    number: "02",
    title: "Active Integrated Work",
    timing: "Months 2–9",
    description:
      "Psychology sessions continue at an appropriate pace. In parallel, your integration practitioner works alongside you — often in your own environment — translating insight into practical routines, system navigation, and real-life capacity. Group programs may be woven in.",
    detail: "Between sessions: brief check-ins, regulation tools, and clear escalation protocols. You're not left alone with your progress.",
  },
  {
    number: "03",
    title: "Consolidation & Step-Down",
    timing: "Months 9+",
    description:
      "Support intensity reduces gradually. Sessions space out. Your integration practitioner shifts to check-ins. The focus is on you holding your own integration — with the knowledge that we're still here.",
    detail: "This isn't a cliff edge. It's a gradual, paced transition designed around your readiness.",
  },
  {
    number: "04",
    title: "Ongoing Relationship",
    timing: "Long-term",
    description:
      "ALIGN• remains a resource beyond active treatment. Annual check-ins, access to workshops and group programs, and a low-friction pathway back into active support if life shifts and you need it again.",
    detail: "Life isn't linear. Your relationship with support shouldn't be either.",
  },
];

function WhatToExpectSection() {
  return (
    <section
      id="whattoexpect"
      data-testid="what-to-expect-section"
      className="py-24 md:py-36 px-6 md:px-12 lg:px-24 bg-[#EAE6DF]/40"
    >
      <div className="max-w-7xl mx-auto">
        <AnimatedSection className="text-center mb-16 md:mb-20">
          <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-6">
            Your Journey
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light tracking-tight text-[#1A1C19] mb-6">
            What to expect
          </h2>
          <p className="text-lg font-light text-[#5C635A] max-w-2xl mx-auto">
            Support at ALIGN&bull; isn't appointment-shaped. It's designed
            around how change actually works — gradually, in context, with
            continuity between sessions.
          </p>
        </AnimatedSection>

        <div className="relative">
          {/* Vertical line connector */}
          <div className="hidden md:block absolute left-[39px] top-8 bottom-8 w-px bg-[#4A6741]/15" />

          <div className="space-y-6">
            {journeyStages.map((stage, index) => (
              <AnimatedSection key={index} delay={index * 0.1}>
                <div
                  className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-4 md:gap-8"
                  data-testid={`journey-stage-${index}`}
                >
                  {/* Number circle */}
                  <div className="flex md:flex-col items-center md:items-center gap-3 md:gap-2">
                    <div className="w-[42px] h-[42px] rounded-full bg-[#4A6741] text-white flex items-center justify-center text-sm font-medium shrink-0 relative z-10">
                      {stage.number}
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#4A6741] md:text-center whitespace-nowrap">
                      {stage.timing}
                    </span>
                  </div>

                  {/* Content card */}
                  <div className="glass-card p-8">
                    <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-normal text-[#1A1C19] mb-3">
                      {stage.title}
                    </h3>
                    <p className="text-sm font-light text-[#5C635A] leading-relaxed mb-4">
                      {stage.description}
                    </p>
                    <div className="bg-[#4A6741]/5 rounded-xl px-5 py-3">
                      <p className="text-xs font-light text-[#4A6741] italic leading-relaxed">
                        {stage.detail}
                      </p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Who We're For Section ─── */
const audiences = [
  {
    tag: "01",
    title: "Neurodivergent Adults",
    subtitle: "AuDHD, ADHD, autism — late-identified or newly diagnosed",
    description:
      "You've done the reading. You understand your brain. But understanding hasn't made the day easier. You're tired of being told to \"try harder\" when the real issue is that generic support doesn't fit how you actually work.",
    needs: [
      "Sense-making beyond labels",
      "Executive functioning & routine scaffolding",
      "Identity integration after late diagnosis",
    ],
    hook: "Insight isn't the finish line. It's the start.",
  },
  {
    tag: "02",
    title: "Parents & Carers",
    subtitle: "Navigating neurodivergence in a child or teen",
    description:
      "You're motivated but exhausted. Coordinating schools, providers, and family life while managing conflicting advice. You don't need another opinion — you need someone who can help make sense of the whole picture and walk alongside you.",
    needs: [
      "Behaviour understood within context",
      "School liaison & systems navigation",
      "Family system support & co-regulation",
    ],
    hook: "You're not failing. The system wasn't built for this.",
  },
  {
    tag: "03",
    title: "Complex Trauma & Fragmented Care",
    subtitle: "Adults navigating multiple services and life stressors",
    description:
      "You're tired of repeating your story. Tired of being bounced between services that don't talk to each other. You need paced, sustained support that understands your context — not another intake form.",
    needs: [
      "Stabilisation & pacing, not quick fixes",
      "Fewer handovers, more coherence",
      "Daily life capacity building",
    ],
    hook: "Support that holds up outside the session.",
  },
  {
    tag: "04",
    title: "Young Adults & Gen Z",
    subtitle: "Emotionally exhausted, overwhelmed, seeking something real",
    description:
      "You're carrying more than people realise. Emotional exhaustion, identity questions, and a world that feels overwhelming — but most support doesn't speak your language or understand your reality.",
    needs: [
      "Emotional regulation & nervous system support",
      "Identity exploration without judgement",
      "Practical tools that fit your actual life",
    ],
    hook: "You're not broken. You just need support that actually fits.",
  },
];

function WhoWereForSection() {
  return (
    <section
      id="whowerefor"
      data-testid="who-were-for-section"
      className="py-24 md:py-36 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <AnimatedSection className="text-center mb-16 md:mb-20">
          <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-6">
            Who We're For
          </p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light tracking-tight text-[#1A1C19] mb-6">
            Created for people navigating
            <br />
            <span className="italic text-[#4A6741]">complexity</span>
          </h2>
          <p className="text-lg font-light text-[#5C635A] max-w-2xl mx-auto">
            You don't need more appointments. You need support that connects the
            dots — between insight and everyday life, between services and your
            actual experience. We also welcome NDIS participants seeking
            integrated, context-aware support.
          </p>
        </AnimatedSection>

        <div className="space-y-8">
          {audiences.map((audience, index) => (
            <AnimatedSection key={index} delay={index * 0.12}>
              <div
                className="glass-card p-8 md:p-10 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-8 lg:gap-12 items-start"
                data-testid={`audience-card-${index}`}
              >
                {/* Left */}
                <div>
                  <span className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] block mb-3">
                    {audience.tag}
                  </span>
                  <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-normal text-[#1A1C19] mb-2 leading-tight">
                    {audience.title}
                  </h3>
                  <p className="text-sm font-light text-[#4A6741] italic mb-6">
                    {audience.subtitle}
                  </p>
                  <div className="hidden lg:block">
                    <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#5C635A] mb-3">
                      What you might need
                    </p>
                    <ul className="space-y-2">
                      {audience.needs.map((need, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm font-light text-[#5C635A]"
                        >
                          <span className="w-1 h-1 rounded-full bg-[#4A6741] mt-2 shrink-0" />
                          {need}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Right */}
                <div>
                  <p className="text-base md:text-lg font-light text-[#5C635A] leading-relaxed mb-6">
                    {audience.description}
                  </p>
                  <div className="lg:hidden mb-6">
                    <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#5C635A] mb-3">
                      What you might need
                    </p>
                    <ul className="space-y-2">
                      {audience.needs.map((need, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-sm font-light text-[#5C635A]"
                        >
                          <span className="w-1 h-1 rounded-full bg-[#4A6741] mt-2 shrink-0" />
                          {need}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-[#4A6741]/5 rounded-2xl px-6 py-4">
                    <p className="font-['Cormorant_Garamond'] text-xl italic text-[#4A6741]">
                      "{audience.hook}"
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Waiting List Form ─── */
function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    reason: "",
    referral_source: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      await axios.post(`${API}/expression-of-interest`, formData);
      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        reason: "",
        referral_source: "",
      });
    } catch (err) {
      setError(
        err.response?.data?.detail || "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const referralOptions = [
    "Search Engine",
    "Social Media",
    "Friend / Family",
    "Healthcare Provider",
    "Other",
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-36 px-6 md:px-12 lg:px-24 bg-[#EAE6DF]/40"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          <AnimatedSection>
            <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-6">
              Join Us
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light tracking-tight text-[#1A1C19] mb-8 leading-[1.1]">
              Join The
              <br />
              <span className="italic text-[#4A6741]">Waiting List</span>
            </h2>
            <div className="section-divider mb-8" />
            <p className="text-lg font-light text-[#5C635A] leading-relaxed mb-10">
              If you'd like to know more about ALIGN&bull; or are interested in
              our services, join our waiting list below. We'll be in touch
              as we prepare to open.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center">
                  <MapPin size={16} className="text-[#4A6741]" />
                </div>
                <span className="text-sm font-light text-[#5C635A]">
                  Perth, Western Australia
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center">
                  <Mail size={16} className="text-[#4A6741]" />
                </div>
                <span className="text-sm font-light text-[#5C635A]">
                  hello@alignpraxis.com.au
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center">
                  <Phone size={16} className="text-[#4A6741]" />
                </div>
                <span className="text-sm font-light text-[#5C635A]">
                  Opening 2026
                </span>
              </div>
              <a
                href="https://www.instagram.com/alignpraxis/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 group"
                data-testid="contact-instagram-link"
              >
                <div className="w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center group-hover:bg-[#4A6741]/20 transition-colors">
                  <Instagram size={16} className="text-[#4A6741]" />
                </div>
                <span className="text-sm font-light text-[#5C635A] group-hover:text-[#4A6741] transition-colors">
                  @alignpraxis
                </span>
              </a>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            {submitted ? (
              <div
                className="glass-card p-10 flex flex-col items-center justify-center text-center min-h-[400px]"
                data-testid="form-success-message"
              >
                <div className="w-16 h-16 rounded-full bg-[#4A6741]/10 flex items-center justify-center mb-6">
                  <Check size={28} className="text-[#4A6741]" />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-3xl font-light text-[#1A1C19] mb-4">
                  Thank you
                </h3>
                <p className="text-sm font-light text-[#5C635A] max-w-sm">
                  Your spot on the waiting list has been secured. We've sent a
                  confirmation to your email. We'll be in touch soon.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn-pill-outline mt-8 text-[11px]"
                  data-testid="submit-another-button"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="glass-card p-8 md:p-10"
                data-testid="eoi-form"
              >
                <div className="space-y-6">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#4A6741] mb-2 block">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input-line"
                      placeholder="Your full name"
                      data-testid="eoi-name-input"
                    />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#4A6741] mb-2 block">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="form-input-line"
                      placeholder="your@email.com"
                      data-testid="eoi-email-input"
                    />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#4A6741] mb-2 block">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input-line"
                      placeholder="+61 XXX XXX XXX"
                      data-testid="eoi-phone-input"
                    />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#4A6741] mb-2 block">
                      Reason for Enquiry
                    </label>
                    <textarea
                      name="reason"
                      value={formData.reason}
                      onChange={handleChange}
                      rows={3}
                      className="form-input-line resize-none"
                      placeholder="Tell us briefly about what you're looking for..."
                      data-testid="eoi-reason-input"
                    />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#4A6741] mb-2 block">
                      How did you hear about us?
                    </label>
                    <select
                      name="referral_source"
                      value={formData.referral_source}
                      onChange={handleChange}
                      className="form-input-line bg-transparent appearance-none cursor-pointer"
                      data-testid="eoi-referral-select"
                    >
                      <option value="">Select an option</option>
                      {referralOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>

                  {error && (
                    <p
                      className="text-red-600 text-sm font-light"
                      data-testid="eoi-error-message"
                    >
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-pill w-full justify-center mt-4"
                    data-testid="submit-interest-form"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Join The Waiting List
                        <Send size={14} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

/* ─── Footer ─── */
function Footer() {
  return (
    <footer
      data-testid="footer"
      className="bg-[#2C4027] text-white py-20 md:py-28 px-6 md:px-12 lg:px-24"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-12 mb-16">
          <div>
            <h3 className="font-['Cormorant_Garamond'] text-4xl font-light mb-6">
              ALIGN<span className="text-[#98A886]">&bull;</span>
            </h3>
            <p className="text-sm font-light text-white/60 leading-relaxed max-w-xs">
              Psychology, in context. Bridging insight with everyday life.
            </p>
            <div className="mt-6">
              <p className="text-[10px] uppercase tracking-[0.2em] font-medium text-white/30 mb-1">
                Professional Enquiries
              </p>
              <p className="text-xs font-light text-white/40">
                Clinicians, referrers, and support coordinators are welcome to reach us at hello@alignpraxis.com.au
              </p>
            </div>
          </div>

          {/* Info Column */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#98A886] mb-6">
              Info
            </p>
            <div className="space-y-3">
              {[
                { label: "Philosophy", id: "philosophy" },
                { label: "Who We're For", id: "whowerefor" },
                { label: "Services", id: "services" },
                { label: "Approach", id: "approach" },
                { label: "What to Expect", id: "whattoexpect" },
                { label: "Contact", id: "contact" },
              ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() =>
                      document
                        .getElementById(item.id)
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                    className="block text-sm font-light text-white/60 hover:text-white transition-colors"
                  >
                    {item.label}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Tools Column */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#98A886] mb-6">
              Tools
            </p>
            <div className="space-y-3">
              <a href="/courses" className="block text-sm font-light text-white/60 hover:text-white transition-colors" data-testid="footer-courses-link">
                Courses
              </a>
              <a href="/resources" className="block text-sm font-light text-white/60 hover:text-white transition-colors" data-testid="footer-resources-link">
                Resources
              </a>
              <a href="/journal" className="block text-sm font-light text-white/60 hover:text-white transition-colors" data-testid="footer-journal-link">
                Journal
              </a>
            </div>
          </div>

          {/* Connect Column */}
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-[#98A886] mb-6">
              Connect
            </p>
            <div className="space-y-3">
              <p className="text-sm font-light text-white/60">
                Perth, Western Australia
              </p>
              <p className="text-sm font-light text-white/60">
                hello@alignpraxis.com.au
              </p>
              <a
                href="https://www.instagram.com/alignpraxis/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm font-light text-white/60 hover:text-white transition-colors"
                data-testid="footer-instagram-link"
              >
                <Instagram size={14} />
                @alignpraxis
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs font-light text-white/40">
            &copy; {new Date().getFullYear()} ALIGN&bull; Integration Psychology
            Practice. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="/privacy"
              className="text-xs font-light text-white/40 hover:text-white/70 transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy Policy
            </a>
            <span className="text-white/20">&middot;</span>
            <p className="text-xs font-light text-white/40">
              Perth &middot; Opening 2026
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─── Main Landing Page ─── */
export default function LandingPage() {
  return (
    <>
      <Helmet>
        <title>
          ALIGN&bull; | Integration Psychology Practice — Perth
        </title>
        <meta
          name="description"
          content="ALIGN• is an integration psychology practice in Perth. We bridge formulation-led psychology, lived experience, and real-life support — so understanding becomes usable and change holds up outside the session."
        />
        <meta
          name="keywords"
          content="psychology, therapy, Perth, mental health, integration practice, formulation-led, neurodivergence, trauma, burnout, ALIGN"
        />
        <meta property="og:title" content="ALIGN• | Psychology, in context" />
        <meta
          property="og:description"
          content="Created for people navigating complexity. Support beyond the session."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="ALIGN• | Integration Psychology Practice"
        />
        <meta
          name="twitter:description"
          content="Psychology, in context. Where understanding becomes usable."
        />
        <link rel="canonical" href="https://www.alignpraxis.com.au" />
      </Helmet>

      <Navigation />
      <HeroSection />
      <PhilosophySection />
      <WhoWereForSection />
      <ServicesSection />
      <ApproachSection />
      <WhatToExpectSection />
      <ContactSection />
      <Footer />
    </>
  );
}
